import { LoaderFunction } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// Auth
import { AuthContextType } from '../../../../../hooks/useAuth';

// Types
import { SurveillanceRoute } from '../../../../../types/surveillanceRoute';

// GQL
import { client } from '../../../../../gql/client';
import { GET_SURVEILLANCE_ROUTE_BY_ID } from '../../../../../gql/queries/surveillanceRoute';

// ═══════════════════════════════════════════════════════════════════
// Loader ligero de la tab "Ordres associades" (feedback usuario 2026-07-07).
//
// Antes reusaba `LoaderRouteDetailed` — el mismo loader pesado de la tab
// Vista prèvia: 2 queries `network-only` (route + segments) + resolución
// completa de headerData (concessió/carretera/equip/operaris/vehicles vía
// N sub-queries). `ChildOrdersPage` solo lee `route.orderIDs[0]` +
// `route.id` + `route.state` — todo lo demás se descartaba tras fetch.
//
// Como React Router mantiene montado el tab ANTERIOR hasta que el loader
// del tab destino resuelve, ese refetch completo (siempre a red, nunca
// cache) bloqueaba la transición — de ahí el "Cargando" percibido al
// cambiar a esta tab.
//
// Fix: loader propio con UNA sola query, `cache-first`. El mismo
// documento (`GET_SURVEILLANCE_ROUTE_BY_ID` + mismas variables) ya lo
// cargó la tab Vista prèvia momentos antes — Apollo lo sirve de caché al
// instante. Deep-link directo a `/child-orders` (caché fría) sigue
// cayendo a red igual que antes, cero regresión en ese caso.
// ═══════════════════════════════════════════════════════════════════

type ChildOrdersLoaderData = {
  route: SurveillanceRoute | null;
  error?: unknown;
  /** Lo que trae el servidor cuando llega; `null` = no se pudo refrescar. */
  fresh?: Promise<ChildOrdersLoaderData | null>;
};

/** El recorrido tal cual está en la caché de Apollo, sin tocar la red. */
const readCachedRoute = (routeId: string): SurveillanceRoute | null => {
  try {
    const cached = client.readQuery({
      query: GET_SURVEILLANCE_ROUTE_BY_ID,
      variables: { id: routeId },
    }) as { surveillanceRoute?: SurveillanceRoute | null } | null;
    return cached?.surveillanceRoute ?? null;
  } catch {
    return null;
  }
};

/**
 * Loader de la pestaña «Órdenes asociadas». Mismo patrón que el del detalle
 * (spec 2026-09-11): el SNAPSHOT de la caché se sirve al instante y el
 * servidor llega después en `fresh`. Hasta el 2026-09-22 leía el recorrido
 * `cache-first`, y con la caché persistida eso significaba NUNCA volver a
 * pedirlo: lo que otra sesión vinculaba o desvinculaba (sala desde el
 * recorrido, la tablet con el autovínculo) no aparecía ni recargando.
 */
export const LoaderRouteChildOrders: (
  userData: AuthContextType | null
) => LoaderFunction =
  (userData) =>
  async ({ params }) => {
    const { routeId } = params;
    if (!routeId || !userData?.user) {
      return { data: Promise.resolve({ route: null }) };
    }

    // La carga de servidor arranca YA; las dos ramas de abajo la comparten.
    const serverPromise: Promise<ChildOrdersLoaderData> = client
      .query<{ surveillanceRoute: SurveillanceRoute | null }>({
        query: GET_SURVEILLANCE_ROUTE_BY_ID,
        variables: { id: routeId },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => ({ route: data?.surveillanceRoute ?? null }));
    // 🔴 `fresh` NUNCA rechaza: `null` significa «manda el snapshot».
    const fresh: Promise<ChildOrdersLoaderData | null> = serverPromise.catch(
      () => null,
    );

    const cachedRoute = readCachedRoute(routeId);
    if (cachedRoute) {
      return { data: Promise.resolve({ route: cachedRoute, fresh }) };
    }

    // Sin caché: como siempre, se espera al servidor.
    const load = async (): Promise<ChildOrdersLoaderData> => {
      try {
        return { ...(await serverPromise), fresh };
      } catch (error) {
        Sentry.captureException(error);
        return { route: null, error, fresh };
      }
    };
    return { data: load() };
  };

export default LoaderRouteChildOrders;
